import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, X, Send, Bot } from 'lucide-react';
import axios from 'axios';
import { API } from '../../constants';

// WhatsApp Widget
export const WhatsAppWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const phoneNumber = '+421915264064';
  const message = 'Dobrý deň, mám záujem o vaše služby.';

  return (
    <div className="whatsapp-widget" data-testid="whatsapp-widget">
      {isOpen && (
        <div className="whatsapp-popup animate-fadeIn">
          <div className="whatsapp-popup-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold">JasnéSvetlá</p>
                <p className="text-xs opacity-80">Zvyčajne odpovedáme do hodiny</p>
              </div>
            </div>
          </div>
          <div className="whatsapp-popup-body">
            <p className="text-zinc-400 text-sm mb-4">
              Ahoj! Ako vám môžeme pomôcť?
            </p>
            <a
              href={`https://wa.me/${phoneNumber.replace(/\+/g, '')}?text=${encodeURIComponent(message)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-chat-btn"
              className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-white py-3 rounded-lg font-medium hover:bg-[#20BD5A] transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              Začať chat
            </a>
          </div>
        </div>
      )}
      
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="whatsapp-btn"
        className="whatsapp-btn animate-pulse-slow"
        aria-label="WhatsApp kontakt"
      >
        <MessageCircle className="w-7 h-7 text-white" />
      </button>
    </div>
  );
};

// AI Chatbot Widget
export const AIChatbot = ({ enabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { type: 'bot', text: 'Ahoj! Som váš virtuálny asistent. Ako vám môžem pomôcť?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { type: 'user', text: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMessage
      });
      setMessages(prev => [...prev, { type: 'bot', text: response.data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: 'Prepáčte, momentálne nemôžem odpovedať. Prosím, kontaktujte nás telefonicky na +421 915 264 064.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  if (!enabled) return null;

  return (
    <div className="fixed bottom-24 right-6 z-50" data-testid="ai-chatbot">
      {isOpen && (
        <div className="w-[350px] bg-[#0a0a12] border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-fadeIn">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white">AI Asistent</p>
                  <p className="text-xs text-white/70">JasneSvetla.sk</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="h-[300px] overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-2xl text-sm ${
                    msg.type === 'user'
                      ? 'bg-[#00BFFF] text-[#05050A] rounded-br-md'
                      : 'bg-white/10 text-white rounded-bl-md'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white/10 p-3 rounded-2xl rounded-bl-md">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-4 border-t border-white/10">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Napíšte správu..."
                className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-[#00BFFF]"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="p-2.5 bg-[#00BFFF] text-[#05050A] rounded-xl hover:bg-[#33CCFF] transition-colors disabled:opacity-50"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </form>
        </div>
      )}

      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="ai-chatbot-btn"
        className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all ${
          isOpen 
            ? 'bg-zinc-700' 
            : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:scale-110'
        }`}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <Bot className="w-6 h-6 text-white" />
        )}
      </button>
    </div>
  );
};

// Cookie Consent Banner
export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) setVisible(true);
  }, []);

  const accept = () => {
    localStorage.setItem('cookie_consent', 'accepted');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div data-testid="cookie-consent" className="fixed bottom-0 left-0 right-0 z-[9999] bg-[#0A0A12] border-t border-white/10 p-4 md:p-6">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-zinc-400 text-sm text-center sm:text-left">
          Táto stránka používa cookies na zlepšenie vášho zážitku. Používaním stránky súhlasíte s ich použitím.
          <Link to="/ochrana-osobnych-udajov" className="text-[#00BFFF] hover:underline ml-1">Viac info</Link>
        </p>
        <button
          data-testid="cookie-accept-btn"
          onClick={accept}
          className="flex-shrink-0 bg-[#00BFFF] text-[#05050A] px-6 py-2.5 text-sm font-semibold hover:bg-[#33CCFF] transition-all"
        >
          Súhlasím
        </button>
      </div>
    </div>
  );
};
